/**
 * Python float floor-division (`a // b`) with CPython's exact semantics.
 *
 * CPython does NOT compute floor(a / b): float_divmod (Objects/floatobject.c) goes
 * through fmod, which is exact, so the quotient never suffers the division-rounding
 * that JS `Math.floor(a / b)` does. The difference is real for this engine:
 *
 *     40.0 // (360/27)            == 2.0   in Python (fmod-based)
 *     Math.floor(40.0 / (360/27)) == 3     in JS (40/13.333333333333334 rounds to 3.0)
 *
 * which flips the Moon's nakshatra at exact-boundary longitudes
 * (caught by fixtures/legacy.json moon_nakshatra_pada @ 40.0). Use this whenever the
 * Python source floor-divides by a divisor that is not exactly representable
 * (360/27, 30/division, ...). Plain `Math.floor(a / b)` stays fine for exact divisors
 * like 30 or 12 whose multiples are exactly representable.
 *
 * (Lives here rather than src/lib/pyutil.ts because that module is frozen for this
 * port cluster.)
 */
export function pyfloordiv(a: number, b: number): number {
  if (b === 0) {
    throw new Error("float floor division by zero");
  }
  // CPython float_divmod:
  //   mod = fmod(vx, wx); div = (vx - mod) / wx;
  //   if (mod) { if ((wx < 0) != (mod < 0)) { mod += wx; div -= 1.0; } }
  //   if (div) { floordiv = floor(div); if (div - floordiv > 0.5) floordiv += 1.0; }
  //   else floordiv = copysign(0.0, vx / wx);
  let mod = a % b; // JS % on floats is fmod
  let div = (a - mod) / b;
  if (mod !== 0) {
    if (b < 0 !== mod < 0) {
      mod += b;
      div -= 1.0;
    }
  }
  if (div !== 0) {
    let floordiv = Math.floor(div);
    if (div - floordiv > 0.5) {
      floordiv += 1.0;
    }
    return floordiv;
  }
  return a / b < 0 ? -0 : 0;
}
